import { useState, useRef, useEffect, useCallback, useMemo } from 'react'
import { Send, Square, Sparkles, Bot, Copy, Check, MessageSquarePlus, Zap, Loader2, Brain, ChevronDown, Paperclip, X, FileText, Upload, Pencil, CheckCircle2, XCircle, FolderPlus, UserPlus, ListChecks, BookOpen, Undo2, Trash2, AlertTriangle } from 'lucide-react'
import { useAuthStore } from '@/stores/authStore'
import { refreshAccessToken } from '@/services/api'
import { useNavigate } from 'react-router-dom'
import { cn } from '@/lib/utils'
import { Badge } from '@/components/common/Badge'

interface MessageAttachment {
  name: string
  type: string
  url?: string // blob URL for image preview
}

interface ActionResult {
  action: string       // 'upsert_client' | 'create_client' | 'create_project' | 'create_task' | 'update_client' | 'update_project' | 'update_task' | 'delete_client' | 'delete_project' | 'delete_task'
  success: boolean
  name: string         // nome del record creato/aggiornato/eliminato
  id?: string          // ID del record
  error?: string
  snapshotId?: string  // presente per azioni update, consente undo
  pendingDeleteId?: string  // presente per azioni delete, richiede conferma
  entityDetails?: Record<string, unknown>
  // Azioni estese che richiedono conferma (finanziario/esterno/ai-cost)
  requiresConfirmation?: boolean
  pendingActionId?: string
  summary?: string
  category?: string
  data?: unknown
  // Super-assistant: disambiguazione + auto-consapevolezza
  needsClarification?: boolean
  options?: { id: string; name: string; hint?: string }[]
  missingFields?: string[]
}

interface StoredFileData {
  data: string       // base64
  mimeType: string
  filename: string
}

interface Message {
  id: string
  role: 'user' | 'assistant'
  content: string
  thinkingContent?: string
  timestamp: Date
  isStreaming?: boolean
  isProcessingActions?: boolean
  durationMs?: number
  model?: string
  tier?: string
  cost?: number
  attachments?: MessageAttachment[]
  originalFiles?: File[] // File objects preservati per consentire edit + reinvio
  fileData?: StoredFileData[] // base64 persistibile per ricostruire File dopo remount
  actionResults?: ActionResult[]
}

/** Mappa model ID → nome breve leggibile */
function shortModelName(model?: string): string {
  if (!model) return ''
  const map: Record<string, string> = {
    'google/gemma-3-27b-it': 'Gemma 27B',
    'meta-llama/llama-3.3-70b-instruct': 'Llama 70B',
    'deepseek/deepseek-r1-0528': 'DeepSeek R1',
    'google/gemini-2.0-flash-exp': 'Gemini Flash',
    'google/gemini-2.5-flash': 'Gemini 2.5',
    'anthropic/claude-opus-4.6': 'Claude Opus 4.6',
    'anthropic/claude-sonnet-4': 'Claude Sonnet',
    'openai/gpt-4o': 'GPT-4o',
    'qwen3:4b': 'Qwen3 4B',
  }
  return map[model] ?? model.split('/').pop()?.replace(':free', '') ?? model
}

/** Etichetta leggibile dell'entità per ogni azione (no più "Progetto" come default). */
function entityLabel(action: string): string {
  const map: Record<string, string> = {
    upsert_client: 'Cliente', create_client: 'Cliente', update_client: 'Cliente', delete_client: 'Cliente',
    create_project: 'Progetto', update_project: 'Progetto', delete_project: 'Progetto',
    create_task: 'Task', update_task: 'Task', delete_task: 'Task',
    create_event: 'Evento', update_event: 'Evento', create_meeting: 'Meeting',
    create_expense: 'Spesa',
    create_quote: 'Preventivo', update_quote_status: 'Preventivo', send_quote: 'Preventivo',
    create_supplier: 'Fornitore', update_supplier: 'Fornitore',
    create_lead: 'Lead', update_lead_status: 'Lead',
    create_content: 'Contenuto', update_content_status: 'Contenuto',
    create_equipment: 'Attrezzatura', checkout_equipment: 'Prestito', return_equipment: 'Reso',
    create_note: 'Nota', create_todo: 'To-do', save_client_note: 'Appunto cliente',
    draft_email: 'Email', send_email: 'Email',
    create_campaign: 'Campagna', update_campaign: 'Campagna', update_campaign_status: 'Campagna',
    create_press_release: 'Comunicato', update_press_release: 'Comunicato', create_media_contact: 'Contatto media',
    create_brand_project: 'Branding', create_brand_deliverable: 'Deliverable',
    create_consulting_project: 'Consulenza', update_consulting_project: 'Consulenza',
    create_consulting_session: 'Sessione', update_consulting_session: 'Sessione',
    create_video_production: 'Produzione', update_video_production: 'Produzione',
    remember_fact: 'Memoria', forget_fact: 'Memoria', update_procedures: 'Procedura',
  }
  if (map[action]) return map[action]
  const base = action.replace(/^(create_|update_|delete_|upsert_|send_|draft_)/, '').replace(/_/g, ' ')
  return base ? base.charAt(0).toUpperCase() + base.slice(1) : 'Azione'
}

/** Colore badge per tier */
function tierStyle(tier?: string) {
  switch (tier) {
    case 'cloud':   return 'bg-success-50 text-success-600 border-success-200'
    case 'gemini':  return 'bg-primary-50 text-primary-600 border-primary-200'
    case 'premium': return 'bg-warning-50 text-warning-600 border-warning-100'
    case 'local':   return 'bg-slate-100 text-slate-600 border-slate-200'
    case 'agent':   return 'bg-violet-50 text-violet-600 border-violet-200'
    default:        return 'bg-slate-100 text-slate-500 border-slate-200'
  }
}

function tierLabel(tier?: string) {
  switch (tier) {
    case 'cloud':   return 'CLOUD'
    case 'gemini':  return 'GEMINI'
    case 'premium': return 'PREMIUM'
    case 'local':   return 'LOCAL'
    case 'agent':   return 'AGENTE V2'
    default:        return ''
  }
}

/**
 * Destinazione del bottone "Vai →" di una card azione: mappa il tipo di
 * azione alla rotta reale del gestionale. Ritorna null quando non c'è una
 * pagina sensata (query di lettura, capability gap, ecc.) → il bottone non
 * viene mostrato. Prima esisteva solo clienti→/clients e TUTTO IL RESTO→
 * /projects (bug: credenziali, eventi, spese, preventivi finivano su Progetti).
 */
function actionTarget(r: ActionResult): string | null {
  const clientId = (r.entityDetails?.clientId as string | undefined) ?? undefined
  switch (r.action) {
    case 'create_client':
    case 'upsert_client':
      return r.id ? `/clients/${r.id}` : '/clients'
    case 'create_project':
      return r.id ? `/projects/${r.id}` : '/projects'
    case 'create_task':
    case 'update_task':
      return '/tasks'
    case 'create_quote':
    case 'update_quote_status':
    case 'send_quote':
      return r.id ? `/preventivi/${r.id}` : '/preventivi'
    case 'create_event':
    case 'update_event':
    case 'create_meeting':
      return '/calendar'
    case 'create_expense':
      return '/accounting'
    case 'create_supplier':
      return '/suppliers'
    case 'create_lead':
      return '/richieste'
    case 'create_content':
      return '/ped'
    case 'create_equipment':
      return r.id ? `/equipment/${r.id}` : '/magazzino'
    case 'create_note':
      return '/notes'
    case 'create_todo':
      return '/todo'
    case 'save_client_credential':
      // Scheda cliente, tab Credenziali (clientId dal backend, non l'id della credenziale)
      return clientId ? `/clients/${clientId}?tab=credentials` : '/clients'
    case 'save_client_note':
      return clientId ? `/clients/${clientId}` : '/clients'
    case 'create_campaign':
      return '/adv'
    case 'create_press_release':
      return '/pr'
    case 'create_brand_project':
      return '/branding'
    case 'create_video_production':
      return '/visual-production'
    default:
      return null
  }
}

const WELCOME_MESSAGE: Message = {
  id: 'welcome',
  role: 'assistant',
  content: 'Ciao! Sono il tuo assistente AI per E-QUIPE ERP. Posso aiutarti con analisi, reportistica, suggerimenti sui clienti e molto altro. Come posso aiutarti oggi?\n\n**Esempi di domande:**\n- "Quali clienti hanno il health score piu\' basso?"\n- "Genera un report sui task in ritardo"\n- "Suggerisci un piano editoriale per Instagram"\n- "Analizza le performance della campagna ADV"',
  timestamp: new Date(),
}

function formatContent(content: string): React.ReactNode {
  // Rimuove l'eventuale riga "[Azioni eseguite nel gestionale: ...]" che il
  // modello a volte scrive da sé (anche su più righe, con parametri grezzi):
  // i risultati reali sono già mostrati nelle card azioni sotto al messaggio,
  // quindi nel testo è solo rumore.
  const cleaned = content.replace(/\n*\[Azioni eseguite nel gestionale:[\s\S]*?\]\s*/gi, '').trimEnd()
  return cleaned.split('\n').map((line, i) => {
    const parts = line.split(/\*\*(.*?)\*\*/g)
    return (
      <p key={i} className={i > 0 ? 'mt-1.5' : ''}>
        {parts.map((part, j) =>
          j % 2 === 1 ? <strong key={j}>{part}</strong> : part
        )}
      </p>
    )
  })
}

const STORAGE_KEY = 'equipe-ai-chat'
const ENGINE_STORAGE_KEY = 'jarvis-ai-engine'

function loadSession(): { messages: Message[]; conversationId: string } {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    if (!raw) return { messages: [WELCOME_MESSAGE], conversationId: Date.now().toString() }
    const data = JSON.parse(raw)
    const msgs: Message[] = (data.messages ?? []).map((m: Record<string, unknown>) => {
      // I blob URL salvati appartengono a una sessione precedente e non sono piu'
      // validi dopo un reload: rimuoverli per evitare immagini rotte.
      const attachments = (m.attachments as MessageAttachment[] | undefined)?.map(
        ({ url: _url, ...rest }) => rest
      )
      return {
        ...m,
        ...(attachments ? { attachments } : {}),
        timestamp: new Date(m.timestamp as string),
        isStreaming: false, // never restore a streaming state
      }
    })
    return { messages: msgs.length ? msgs : [WELCOME_MESSAGE], conversationId: data.conversationId ?? Date.now().toString() }
  } catch {
    return { messages: [WELCOME_MESSAGE], conversationId: Date.now().toString() }
  }
}

export function AIAssistantPage() {
  const user = useAuthStore((state) => state.user)
  const token = useAuthStore((state) => state.tokens?.accessToken)
  const navigate = useNavigate()
  const [messages, setMessages] = useState<Message[]>(() => loadSession().messages)
  const [input, setInput] = useState('')
  const [isStreaming, setIsStreaming] = useState(false)
  const [conversationId, setConversationId] = useState(() => loadSession().conversationId)
  // Motore AI v2 (loop agentico unico), dietro flag lato client: forza
  // `engine: 'v2'` nel body della richiesta. Assente = decide il server.
  const [agentV2, setAgentV2] = useState(() => localStorage.getItem(ENGINE_STORAGE_KEY) === 'v2')
  const [copiedId, setCopiedId] = useState<string | null>(null)
  const [expandedThinking, setExpandedThinking] = useState<Set<string>>(new Set())
  const [pendingFiles, setPendingFiles] = useState<File[]>([])
  const [isDragging, setIsDragging] = useState(false)
  const [rolledBackSnapshots, setRolledBackSnapshots] = useState<Set<string>>(new Set())
  const [rollingBack, setRollingBack] = useState<string | null>(null)
  const [confirmedDeletes, setConfirmedDeletes] = useState<Map<string, string>>(new Map()) // pendingDeleteId → snapshotId
  const [confirmingDelete, setConfirmingDelete] = useState<string | null>(null)
  // Azioni estese in attesa di conferma: pendingActionId → esito
  const [actionStatus, setActionStatus] = useState<Map<string, 'confirmed' | 'cancelled'>>(new Map())
  const [confirmingAction, setConfirmingAction] = useState<string | null>(null)
  const dragCounterRef = useRef(0)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const abortRef = useRef<AbortController | null>(null)
  // Registro dei blob URL creati per gli allegati dei messaggi utente,
  // così da poterli revocare al unmount ed evitare memory leak.
  const attachmentUrlsRef = useRef<Set<string>>(new Set())

  // Revoca tutti i blob URL degli allegati al unmount del componente.
  useEffect(() => {
    const urls = attachmentUrlsRef.current
    return () => {
      urls.forEach((url) => URL.revokeObjectURL(url))
      urls.clear()
    }
  }, [])

  // Blob URL per le anteprime dei file pendenti — creati una sola volta per file
  // e revocati quando i file cambiano / al unmount, per evitare memory leak.
  const pendingPreviews = useMemo(
    () =>
      pendingFiles.map((file) =>
        file.type.startsWith('image/') ? URL.createObjectURL(file) : null
      ),
    [pendingFiles]
  )
  useEffect(() => {
    return () => {
      pendingPreviews.forEach((url) => {
        if (url) URL.revokeObjectURL(url)
      })
    }
  }, [pendingPreviews])

  // Persist chat to sessionStorage on every message change.
  // ⚠️ Robusto contro QuotaExceededError: gli allegati base64 (attachments.data
  // e fileData) possono superare il limite di sessionStorage (~5MB) e far
  // crashare l'app. Qui degradiamo progressivamente invece di lanciare:
  // pieno → senza fileData → senza base64 allegati → solo ultimi 30 messaggi →
  // pulizia. La conversazione resta comunque salvata lato server.
  useEffect(() => {
    const slim = messages.map(({ originalFiles: _o, ...rest }) => rest)
    const tryStore = (msgs: unknown): boolean => {
      try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ messages: msgs, conversationId }))
        return true
      } catch {
        return false
      }
    }
    // 1) tentativo completo
    if (tryStore(slim)) return
    // 2) senza fileData (il più pesante: serve solo per ri-allegare dopo reload)
    const noFileData = slim.map(({ fileData: _f, ...r }) => r)
    if (tryStore(noFileData)) return
    // 3) senza i base64 degli allegati (manteniamo nome/tipo per l'etichetta)
    const noAttachData = noFileData.map((m) =>
      m.attachments && m.attachments.length > 0
        ? { ...m, attachments: m.attachments.map((a) => ({ ...a, data: '' })) }
        : m
    )
    if (tryStore(noAttachData)) return
    // 4) solo gli ultimi 30 messaggi, già alleggeriti
    if (tryStore(noAttachData.slice(-30))) return
    // 5) rinuncia: rimuovi la chiave per non lasciare dati corrotti/enormi
    try { sessionStorage.removeItem(STORAGE_KEY) } catch { /* noop */ }
  }, [messages, conversationId])

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, isStreaming])

  const sendMessage = useCallback(async (messageText: string) => {
    if ((!messageText.trim() && pendingFiles.length === 0) || isStreaming) return
    // Se ci sono solo file senza testo, usa un prompt generico
    const finalText = messageText.trim() || 'Analizza questo file'

    // Catturare i file pendenti e creare preview per il messaggio utente
    const filesToSend = [...pendingFiles]
    const messageAttachments: MessageAttachment[] = filesToSend.map((f) => {
      const att: MessageAttachment = { name: f.name, type: f.type }
      if (f.type.startsWith('image/')) {
        att.url = URL.createObjectURL(f)
        attachmentUrlsRef.current.add(att.url)
      }
      return att
    })

    const userMsgId = Date.now().toString()
    const userMessage: Message = {
      id: userMsgId,
      role: 'user',
      content: finalText,
      timestamp: new Date(),
      ...(messageAttachments.length > 0 ? { attachments: messageAttachments } : {}),
      ...(filesToSend.length > 0 ? { originalFiles: filesToSend } : {}),
    }

    const assistantId = (Date.now() + 1).toString()
    const assistantMessage: Message = {
      id: assistantId,
      role: 'assistant',
      content: '',
      timestamp: new Date(),
      isStreaming: true,
    }

    setMessages((prev) => [...prev, userMessage, assistantMessage])
    setInput('')
    setPendingFiles([])
    setIsStreaming(true)

    const controller = new AbortController()
    abortRef.current = controller

    try {
      const baseUrl = import.meta.env.VITE_API_URL ?? '/api/v1'

      // Convertire file in base64 per invio JSON (evita problemi multipart/multer)
      const base64Attachments = await Promise.all(
        filesToSend.map((f) =>
          new Promise<{ data: string; mimeType: string; filename: string }>((resolve, reject) => {
            const reader = new FileReader()
            reader.onload = () => {
              const result = reader.result as string
              // Rimuovere il prefisso "data:...;base64,"
              const base64 = result.split(',')[1] ?? ''
              resolve({ data: base64, mimeType: f.type, filename: f.name })
            }
            reader.onerror = reject
            reader.readAsDataURL(f)
          })
        )
      )

      // Salvare i dati base64 nel messaggio utente per consentire edit dopo remount
      if (base64Attachments.length > 0) {
        setMessages((prev) =>
          prev.map((m) =>
            m.id === userMsgId ? { ...m, fileData: base64Attachments } : m
          )
        )
      }

      // Costruire cronologia conversazione per la memoria del chatbot
      // Include action results nel contenuto assistant per ricordare le azioni eseguite
      const chatHistory = messages
        .filter(m => m.id !== 'welcome' && m.content)
        .map(m => {
          let content = m.content
          if (m.role === 'assistant' && m.actionResults?.length) {
            const actions = m.actionResults
              .map(r => r.success
                ? `${entityLabel(r.action)}: ${r.name}${r.id ? ` (ID: ${r.id})` : ''}`
                : `ERRORE ${r.action}: ${r.error ?? r.name}`)
              .join('; ')
            content += `\n\n[Azioni eseguite nel gestionale: ${actions}]`
          }
          return { role: m.role, content }
        })

      // Sempre JSON — nessun multipart/FormData
      const buildFetchOptions = (authToken: string): RequestInit => ({
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({
          message: finalText,
          conversationId,
          ...(base64Attachments.length > 0 ? { attachments: base64Attachments } : {}),
          ...(chatHistory.length > 0 ? { history: chatHistory } : {}),
          ...(agentV2 ? { engine: 'v2' } : {}),
        }),
        signal: controller.signal,
      })

      let currentToken = token ?? ''
      let response = await fetch(`${baseUrl}/ai/chat/stream`, buildFetchOptions(currentToken))

      // Se 401 (token scaduto), prova refresh e ritenta una volta
      if (response.status === 401) {
        try {
          currentToken = await refreshAccessToken()
          response = await fetch(`${baseUrl}/ai/chat/stream`, buildFetchOptions(currentToken))
        } catch {
          setMessages((prev) =>
            prev.map((m) =>
              m.id === assistantId ? { ...m, content: 'Sessione scaduta. Effettua nuovamente il login.', isStreaming: false } : m
            )
          )
          setIsStreaming(false)
          return
        }
      }

      if (!response.ok) {
        let errText: string
        if (response.status === 503) {
          errText = 'Servizio AI non disponibile. Verifica che Ollama sia in esecuzione.'
        } else {
          // Prova a leggere il body JSON per un messaggio più specifico
          try {
            const errBody = await response.json()
            errText = errBody?.error?.message || errBody?.message || `Errore ${response.status}: ${response.statusText}`
          } catch {
            errText = `Errore ${response.status}: ${response.statusText}`
          }
        }
        setMessages((prev) =>
          prev.map((m) =>
            m.id === assistantId ? { ...m, content: errText, isStreaming: false } : m
          )
        )
        setIsStreaming(false)
        return
      }

      const reader = response.body?.getReader()
      if (!reader) throw new Error('No reader')

      const decoder = new TextDecoder()
      let buffer = ''

      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        buffer += decoder.decode(value, { stream: true })

        // Parse SSE events from buffer
        const lines = buffer.split('\n')
        buffer = lines.pop() ?? ''

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue
          const jsonStr = line.slice(6)
          if (!jsonStr) continue

          try {
            const event = JSON.parse(jsonStr)

            if (event.type === 'thinking') {
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === assistantId
                    ? { ...m, thinkingContent: (m.thinkingContent ?? '') + event.content }
                    : m
                )
              )
            } else if (event.type === 'token') {
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === assistantId
                    ? { ...m, content: m.content + event.content }
                    : m
                )
              )
            } else if (event.type === 'done') {
              // Adotta l'id REALE della conversazione creato/risolto dal server:
              // senza questo, il client continua a mandare il proprio id fittizio
              // (Date.now) e il backend apre una Conversation nuova a ogni turno
              // → il v2 riparte senza storia ("perde il contesto").
              if (event.conversationId) setConversationId(event.conversationId)
              setMessages((prev) =>
                prev.map((m) => {
                  if (m.id !== assistantId) return m
                  const finalContent = event.content || m.content
                  // If model only produced thinking with no visible answer, show fallback
                  const fallback = !finalContent && m.thinkingContent
                    ? 'Ho analizzato i dati ma non sono riuscito a formulare una risposta. Prova a riformulare la domanda.'
                    : finalContent
                  return { ...m, content: fallback, isStreaming: false, durationMs: event.durationMs, model: event.model, tier: event.tier, cost: event.cost }
                })
              )
            } else if (event.type === 'error') {
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === assistantId
                    ? { ...m, content: `Errore: ${event.message}`, isStreaming: false }
                    : m
                )
              )
            } else if (event.type === 'actions_start') {
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === assistantId
                    ? { ...m, isProcessingActions: true }
                    : m
                )
              )
            } else if (event.type === 'actions_found') {
              // Noop — il count è informativo, lo spinner è già attivo
            } else if (event.type === 'action_result') {
              const result: ActionResult = {
                action: event.action,
                success: event.success,
                name: event.name,
                id: event.id,
                error: event.error,
                snapshotId: event.snapshotId,
                pendingDeleteId: event.pendingDeleteId,
                entityDetails: event.entityDetails,
                requiresConfirmation: event.requiresConfirmation,
                pendingActionId: event.pendingActionId,
                summary: event.summary,
                category: event.category,
                data: event.data,
                needsClarification: event.needsClarification,
                options: event.options,
                missingFields: event.missingFields,
              }
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === assistantId
                    ? { ...m, actionResults: [...(m.actionResults ?? []), result] }
                    : m
                )
              )
            } else if (event.type === 'actions_complete') {
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === assistantId
                    ? { ...m, isProcessingActions: false }
                    : m
                )
              )
            } else if (event.type === 'actions_error') {
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === assistantId
                    ? { ...m, isProcessingActions: false }
                    : m
                )
              )
            }
          } catch {
            // ignore malformed JSON
          }
        }
      }
    } catch (error: unknown) {
      if (error instanceof Error && error.name === 'AbortError') return
      setMessages((prev) =>
        prev.map((m) =>
          m.id === assistantId
            ? { ...m, content: `Errore di connessione: ${error instanceof Error ? error.message : 'sconosciuto'}`, isStreaming: false }
            : m
        )
      )
    } finally {
      setIsStreaming(false)
      abortRef.current = null
    }
  }, [isStreaming, token, conversationId, pendingFiles, agentV2])

  function handleSend() {
    sendMessage(input)
  }

  function handleStop() {
    abortRef.current?.abort()
    abortRef.current = null
    setIsStreaming(false)
    // Marca l'eventuale messaggio assistant in streaming come concluso
    setMessages((prev) =>
      prev.map((m) =>
        m.isStreaming
          ? { ...m, isStreaming: false, isProcessingActions: false }
          : m
      )
    )
  }

  function autoGrowTextarea(el: HTMLTextAreaElement) {
    el.style.height = 'auto'
    el.style.height = `${Math.min(el.scrollHeight, 128)}px`
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  // Reset altezza textarea quando l'input viene svuotato (invio/clear/edit)
  useEffect(() => {
    const el = inputRef.current
    if (!el) return
    if (input === '') {
      el.style.height = 'auto'
    } else {
      autoGrowTextarea(el)
    }
  }, [input])

  const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'application/pdf']
  const MAX_FILE_SIZE = 20 * 1024 * 1024 // 20MB

  function addValidFiles(files: File[]) {
    const valid = files.filter((f) => ALLOWED_TYPES.includes(f.type) && f.size <= MAX_FILE_SIZE)
    if (valid.length > 0) {
      setPendingFiles((prev) => [...prev, ...valid].slice(0, 5))
    }
  }

  function handleFileSelect(e: React.ChangeEvent<HTMLInputElement>) {
    addValidFiles(Array.from(e.target.files ?? []))
    e.target.value = ''
  }

  function removePendingFile(index: number) {
    setPendingFiles((prev) => prev.filter((_, i) => i !== index))
  }

  // ── Drag & Drop ──
  function handleDragEnter(e: React.DragEvent) {
    e.preventDefault()
    e.stopPropagation()
    dragCounterRef.current += 1
    if (e.dataTransfer.types.includes('Files')) {
      setIsDragging(true)
    }
  }

  function handleDragLeave(e: React.DragEvent) {
    e.preventDefault()
    e.stopPropagation()
    dragCounterRef.current -= 1
    if (dragCounterRef.current === 0) {
      setIsDragging(false)
    }
  }

  function handleDragOver(e: React.DragEvent) {
    e.preventDefault()
    e.stopPropagation()
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault()
    e.stopPropagation()
    dragCounterRef.current = 0
    setIsDragging(false)
    const files = Array.from(e.dataTransfer.files)
    addValidFiles(files)
    inputRef.current?.focus()
  }

  function handleNewChat() {
    if (abortRef.current) abortRef.current.abort()
    // Revoca i blob URL degli allegati: i messaggi vengono scartati.
    attachmentUrlsRef.current.forEach((url) => URL.revokeObjectURL(url))
    attachmentUrlsRef.current.clear()
    sessionStorage.removeItem(STORAGE_KEY)
    setMessages([WELCOME_MESSAGE])
    setConversationId(Date.now().toString())
    setIsStreaming(false)
    setPendingFiles([])
  }

  function editMessage(messageId: string) {
    if (isStreaming) return
    const idx = messages.findIndex((m) => m.id === messageId)
    if (idx === -1) return
    const msg = messages[idx]!
    // Metti il testo nel campo input, ripristina i file e taglia la conversazione
    setInput(msg.content)

    // Ripristina i file: usa originalFiles se disponibili (in-memory),
    // altrimenti ricostruisci da fileData (base64 persistito in sessionStorage)
    if (msg.originalFiles && msg.originalFiles.length > 0) {
      setPendingFiles(msg.originalFiles)
    } else if (msg.fileData && msg.fileData.length > 0) {
      const reconstructed = msg.fileData.map((fd) => {
        const byteString = atob(fd.data)
        const bytes = new Uint8Array(byteString.length)
        for (let i = 0; i < byteString.length; i++) {
          bytes[i] = byteString.charCodeAt(i)
        }
        return new File([bytes], fd.filename, { type: fd.mimeType })
      })
      setPendingFiles(reconstructed)
    } else {
      setPendingFiles([])
    }

    setMessages((prev) => prev.slice(0, idx))
    // Focus sull'input dopo il render
    setTimeout(() => inputRef.current?.focus(), 0)
  }

  async function handleRollback(snapshotId: string) {
    if (rollingBack) return
    setRollingBack(snapshotId)
    try {
      const baseUrl = import.meta.env.VITE_API_URL ?? '/api/v1'
      let currentToken = token ?? ''
      let response = await fetch(`${baseUrl}/ai/rollback/${snapshotId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${currentToken}`,
        },
      })
      if (response.status === 401) {
        try {
          currentToken = await refreshAccessToken()
          response = await fetch(`${baseUrl}/ai/rollback/${snapshotId}`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${currentToken}`,
            },
          })
        } catch {
          return
        }
      }
      if (response.ok) {
        setRolledBackSnapshots((prev) => new Set(prev).add(snapshotId))
      }
    } catch {
      // silently fail
    } finally {
      setRollingBack(null)
    }
  }

  async function handleConfirmDelete(pendingDeleteId: string) {
    if (confirmingDelete) return
    setConfirmingDelete(pendingDeleteId)
    try {
      const baseUrl = import.meta.env.VITE_API_URL ?? '/api/v1'
      let currentToken = token ?? ''
      let response = await fetch(`${baseUrl}/ai/delete/confirm/${pendingDeleteId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${currentToken}`,
        },
      })
      if (response.status === 401) {
        try {
          currentToken = await refreshAccessToken()
          response = await fetch(`${baseUrl}/ai/delete/confirm/${pendingDeleteId}`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${currentToken}`,
            },
          })
        } catch {
          return
        }
      }
      if (response.ok) {
        const data = await response.json()
        const snapshotId = data?.data?.snapshotId as string
        setConfirmedDeletes((prev) => new Map(prev).set(pendingDeleteId, snapshotId))
      }
    } catch {
      // silently fail
    } finally {
      setConfirmingDelete(null)
    }
  }

  async function handleConfirmAction(pendingActionId: string) {
    if (confirmingAction) return
    setConfirmingAction(pendingActionId)
    try {
      const baseUrl = import.meta.env.VITE_API_URL ?? '/api/v1'
      let currentToken = token ?? ''
      const doFetch = (t: string) => fetch(`${baseUrl}/ai/action/confirm/${pendingActionId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${t}` },
      })
      let response = await doFetch(currentToken)
      if (response.status === 401) {
        try { currentToken = await refreshAccessToken(); response = await doFetch(currentToken) } catch { return }
      }
      if (response.ok) {
        setActionStatus((prev) => new Map(prev).set(pendingActionId, 'confirmed'))
      }
    } catch {
      // silently fail
    } finally {
      setConfirmingAction(null)
    }
  }

  async function handleCancelAction(pendingActionId: string) {
    try {
      const baseUrl = import.meta.env.VITE_API_URL ?? '/api/v1'
      await fetch(`${baseUrl}/ai/action/cancel/${pendingActionId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token ?? ''}` },
      })
    } catch {
      // silently fail
    }
    setActionStatus((prev) => new Map(prev).set(pendingActionId, 'cancelled'))
  }

  const userInitials = user
    ? `${user.firstName.charAt(0)}${user.lastName.charAt(0)}`.toUpperCase()
    : 'U'

  return (
    <div
      className="flex flex-col h-[calc(100vh-8rem)] space-y-0 relative"
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {/* Drop overlay */}
      {isDragging && (
        <div className="absolute inset-0 z-50 bg-primary-50 border-2 border-dashed border-primary-400 rounded-2xl flex items-center justify-center pointer-events-none">
          <div className="flex flex-col items-center gap-2 bg-white px-6 py-4 rounded-2xl border border-slate-200 shadow-modal">
            <Upload className="w-8 h-8 text-primary-600" />
            <p className="text-sm font-medium text-slate-900">Rilascia file qui</p>
            <p className="text-xs text-slate-500">Immagini o PDF (max 20MB)</p>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex-shrink-0 mb-4">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-primary-50 rounded-lg flex items-center justify-center flex-shrink-0">
            <Sparkles className="w-5 h-5 text-primary-600" />
          </div>
          <div className="min-w-0">
            <h1 className="text-2xl font-semibold tracking-tight text-slate-900">AI Assistant</h1>
            <p className="text-sm text-slate-500">Assistente intelligente per E-QUIPE ERP</p>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <button
              onClick={() => {
                const next = !agentV2
                setAgentV2(next)
                if (next) localStorage.setItem(ENGINE_STORAGE_KEY, 'v2')
                else localStorage.removeItem(ENGINE_STORAGE_KEY)
              }}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors',
                agentV2
                  ? 'border-primary-200 bg-primary-50 text-primary-700 hover:bg-primary-100'
                  : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
              )}
              title="Forza il nuovo motore agentico per questa conversazione"
            >
              <Zap className="w-4 h-4" />
              Agente v2
              <Badge variant="warning" size="sm">beta</Badge>
            </button>
            <button
              onClick={handleNewChat}
              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors"
            >
              <MessageSquarePlus className="w-4 h-4" />
              Nuova chat
            </button>
            <Badge variant="warning" dot>Beta</Badge>
          </div>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto bg-white rounded-2xl border border-slate-200 shadow-card p-4 space-y-4">
        {messages.map((message, msgIndex) => {
          const isError = message.role === 'assistant' && (
            message.content.startsWith('Errore') ||
            message.content.startsWith('Servizio AI non disponibile')
          )

          return (
            <div
              key={message.id}
              className={cn(
                'flex items-start gap-3',
                message.role === 'user' && 'flex-row-reverse'
              )}
            >
              {/* Avatar */}
              {message.role === 'assistant' ? (
                <div className="w-7 h-7 bg-primary-50 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Bot className="w-4 h-4 text-primary-600" />
                </div>
              ) : (
                <div className="w-7 h-7 bg-slate-200 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-[11px] font-semibold text-slate-600">{userInitials}</span>
                </div>
              )}

              {/* Bubble */}
              <div
                className={cn(
                  'relative group max-w-[75%] rounded-xl px-4 py-3 text-sm leading-relaxed',
                  message.role === 'assistant'
                    ? isError
                      ? 'bg-danger-50 border border-danger-200 text-danger-700'
                      : 'bg-slate-50 border border-slate-200 text-slate-800'
                    : 'bg-primary-600 text-white'
                )}
              >
                {/* Thinking box */}
                {message.thinkingContent && (
                  <div className="mb-2">
                    <button
                      onClick={() => setExpandedThinking((prev) => {
                        const next = new Set(prev)
                        if (next.has(message.id)) next.delete(message.id)
                        else next.add(message.id)
                        return next
                      })}
                      className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-700 transition-colors"
                    >
                      <Brain className="w-3.5 h-3.5 text-accent-400" />
                      <span>Ragionamento</span>
                      <ChevronDown className={cn(
                        'w-3 h-3 transition-transform',
                        (expandedThinking.has(message.id) || (message.isStreaming && !message.content)) && 'rotate-180'
                      )} />
                      {message.isStreaming && !message.content && (
                        <Loader2 className="w-3 h-3 animate-spin text-accent-400" />
                      )}
                    </button>
                    {(expandedThinking.has(message.id) || (message.isStreaming && !message.content)) && (
                      <div className="mt-1.5 pl-3 border-l-2 border-accent-400 bg-slate-50 rounded-r-md py-2 pr-2">
                        <p className="text-xs text-slate-500 font-mono whitespace-pre-wrap leading-relaxed">
                          {message.thinkingContent}
                          {message.isStreaming && !message.content && (
                            <span className="inline-block w-1 h-3 bg-accent-400 animate-pulse ml-0.5 align-text-bottom rounded-sm" />
                          )}
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {message.content ? formatContent(message.content) : null}

                {/* Allegati nel messaggio utente */}
                {message.attachments && message.attachments.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {message.attachments.map((att, i) =>
                      att.url ? (
                        <img
                          key={i}
                          src={att.url}
                          alt={att.name}
                          className="w-20 h-20 object-cover rounded-md border border-slate-200"
                        />
                      ) : (
                        <div
                          key={i}
                          className={cn(
                            'flex items-center gap-1.5 px-2 py-1 rounded-md text-[11px]',
                            message.role === 'user'
                              ? 'bg-white/10 text-white/80'
                              : 'bg-slate-100 text-slate-600'
                          )}
                        >
                          <FileText className="w-3.5 h-3.5" />
                          <span className="truncate max-w-[120px]">{att.name}</span>
                        </div>
                      )
                    )}
                  </div>
                )}

                {/* Action results */}
                {message.isProcessingActions && !message.actionResults?.length && (
                  <div className="flex items-center gap-2 mt-3 py-2 px-3 bg-warning-50 border border-warning-100 rounded-md">
                    <Loader2 className="w-3.5 h-3.5 animate-spin text-warning-600" />
                    <span className="text-xs text-warning-600 font-medium">Creazione in corso...</span>
                  </div>
                )}
                {message.actionResults && message.actionResults.length > 0 && (
                  <div className="mt-3 space-y-1.5">
                    {message.actionResults.map((result, i) => {
                      const isUpdate = result.action.startsWith('update_') && result.action !== 'update_procedures'
                      const isDelete = result.action.startsWith('delete_')
                      const isRolledBack = result.snapshotId ? rolledBackSnapshots.has(result.snapshotId) : false
                      const isRollingBackThis = result.snapshotId ? rollingBack === result.snapshotId : false

                      // Delete state tracking
                      const deleteConfirmed = result.pendingDeleteId ? confirmedDeletes.has(result.pendingDeleteId) : false
                      const deleteSnapshotId = result.pendingDeleteId ? confirmedDeletes.get(result.pendingDeleteId) : undefined
                      const deleteRolledBack = deleteSnapshotId ? rolledBackSnapshots.has(deleteSnapshotId) : false
                      const isConfirmingThis = result.pendingDeleteId ? confirmingDelete === result.pendingDeleteId : false
                      const isRollingBackDelete = deleteSnapshotId ? rollingBack === deleteSnapshotId : false

                      // Azioni estese che richiedono conferma (preventivi/spese/email/...)
                      if (result.requiresConfirmation && result.pendingActionId) {
                        const stat = actionStatus.get(result.pendingActionId)
                        const isConfirmingThisAction = confirmingAction === result.pendingActionId
                        return (
                          <div
                            key={i}
                            className={cn(
                              'flex flex-col gap-2 py-2 px-3 rounded-xl text-xs border',
                              stat === 'confirmed' ? 'bg-success-50 border-success-200'
                                : stat === 'cancelled' ? 'bg-slate-50 border-slate-200'
                                  : 'bg-warning-50 border-warning-200'
                            )}
                          >
                            <div className="flex items-start gap-2">
                              {stat === 'confirmed'
                                ? <Check className="w-4 h-4 text-success-600 mt-0.5 flex-shrink-0" />
                                : <AlertTriangle className={cn('w-4 h-4 mt-0.5 flex-shrink-0', stat === 'cancelled' ? 'text-slate-400' : 'text-warning-600')} />}
                              <span className={stat === 'cancelled' ? 'text-slate-400' : stat === 'confirmed' ? 'text-success-700' : 'text-warning-700'}>
                                {stat === 'confirmed'
                                  ? `Confermato: ${result.summary ?? result.name}`
                                  : stat === 'cancelled'
                                    ? `Annullato: ${result.summary ?? result.name}`
                                    : (result.summary ?? 'Confermi questa azione?')}
                              </span>
                            </div>
                            {!stat && (
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => handleConfirmAction(result.pendingActionId!)}
                                  disabled={isConfirmingThisAction}
                                  className="flex items-center gap-1 text-[11px] text-white bg-primary-600 hover:bg-primary-700 px-3 py-1 rounded-lg transition-colors disabled:opacity-50"
                                >
                                  {isConfirmingThisAction ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                                  Conferma
                                </button>
                                <button
                                  onClick={() => handleCancelAction(result.pendingActionId!)}
                                  disabled={isConfirmingThisAction}
                                  className="flex items-center gap-1 text-[11px] text-slate-600 bg-white border border-slate-300 hover:bg-slate-50 px-3 py-1 rounded-lg transition-colors disabled:opacity-50"
                                >
                                  Annulla
                                </button>
                              </div>
                            )}
                          </div>
                        )
                      }

                      // Auto-consapevolezza: funzione non disponibile → proposta di implementazione
                      if (result.action === 'capability_gap') {
                        return (
                          <div key={i} className="flex flex-col gap-1.5 py-2 px-3 rounded-xl text-xs border bg-violet-50 border-violet-200">
                            <div className="flex items-start gap-2">
                              <Sparkles className="w-4 h-4 text-violet-600 mt-0.5 flex-shrink-0" />
                              <span className="text-violet-700 font-medium">{result.name}</span>
                            </div>
                            {result.summary && <p className="text-violet-700/80 pl-6">{result.summary}</p>}
                            <p className="text-[11px] text-violet-500 pl-6">Segnalata come proposta di implementazione.</p>
                          </div>
                        )
                      }

                      // Disambiguazione: più entità corrispondono → l'utente sceglie
                      if (result.needsClarification && result.options && result.options.length > 0) {
                        return (
                          <div key={i} className="flex flex-col gap-2 py-2 px-3 rounded-xl text-xs border bg-amber-50 border-amber-200">
                            <div className="flex items-start gap-2">
                              <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                              <span className="text-amber-700">{result.error ?? 'Quale intendi?'}</span>
                            </div>
                            <div className="flex flex-col gap-1 pl-6">
                              {result.options.map((opt) => (
                                <button
                                  key={opt.id}
                                  onClick={() => {
                                    // Re-invia la DOMANDA ORIGINALE arricchita col record scelto,
                                    // così mantiene l'intento (es. "controlla le spese") e il verbo
                                    // di lettura → riparte la pipeline tool e risolve in modo univoco.
                                    // Senza contesto originale, fallback al vecchio "Intendo ...".
                                    const priorUser = messages.slice(0, msgIndex).reverse().find((m) => m.role === 'user')
                                    const chosen = opt.hint ? `"${opt.name}" (${opt.hint})` : `"${opt.name}"`
                                    const text = priorUser?.content
                                      ? `${priorUser.content} — intendi esattamente questo record, usa il nome esatto ${chosen}`
                                      : `Intendo ${chosen}`
                                    sendMessage(text)
                                  }}
                                  className="text-left text-[11px] text-amber-800 bg-white border border-amber-300 hover:bg-amber-100 px-2.5 py-1 rounded-lg transition-colors"
                                >
                                  {opt.name}{opt.hint ? <span className="text-amber-500"> — {opt.hint}</span> : null}
                                </button>
                              ))}
                            </div>
                          </div>
                        )
                      }

                      // Delete actions have special rendering
                      if (isDelete && result.success && result.pendingDeleteId) {
                        return (
                          <div
                            key={i}
                            className={cn(
                              'flex items-center gap-2 py-1.5 px-3 rounded-md text-xs',
                              deleteRolledBack
                                ? 'bg-slate-50 border border-slate-200'
                                : deleteConfirmed
                                  ? 'bg-danger-50 border border-danger-200'
                                  : 'bg-warning-50 border border-warning-100'
                            )}
                          >
                            {deleteRolledBack ? (
                              <Check className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                            ) : deleteConfirmed ? (
                              <Trash2 className="w-3.5 h-3.5 text-danger-600 flex-shrink-0" />
                            ) : (
                              <AlertTriangle className="w-3.5 h-3.5 text-warning-600 flex-shrink-0" />
                            )}
                            <Trash2 className="w-3.5 h-3.5 text-slate-500 flex-shrink-0" />
                            <span className={
                              deleteRolledBack
                                ? 'text-slate-400'
                                : deleteConfirmed
                                  ? 'text-danger-700 line-through'
                                  : 'text-warning-600'
                            }>
                              {deleteRolledBack
                                ? `Ripristinato: ${result.action === 'delete_client' ? 'Cliente' : result.action === 'delete_project' ? 'Progetto' : 'Task'}: ${result.name}`
                                : deleteConfirmed
                                  ? `Eliminato: ${result.action === 'delete_client' ? 'Cliente' : result.action === 'delete_project' ? 'Progetto' : 'Task'}: ${result.name}`
                                  : `Eliminare ${result.action === 'delete_client' ? 'Cliente' : result.action === 'delete_project' ? 'Progetto' : 'Task'}: ${result.name}?`
                              }
                            </span>
                            {/* Pending: show confirm button */}
                            {!deleteConfirmed && !deleteRolledBack && (
                              <button
                                onClick={() => handleConfirmDelete(result.pendingDeleteId!)}
                                disabled={isConfirmingThis}
                                className="ml-auto flex items-center gap-1 text-[10px] text-danger-600 hover:text-danger-700 font-medium bg-danger-50 hover:bg-danger-100 px-2 py-0.5 rounded transition-colors disabled:opacity-50"
                              >
                                {isConfirmingThis ? (
                                  <Loader2 className="w-3 h-3 animate-spin" />
                                ) : (
                                  <Trash2 className="w-3 h-3" />
                                )}
                                Conferma eliminazione
                              </button>
                            )}
                            {/* Confirmed: show undo button */}
                            {deleteConfirmed && !deleteRolledBack && deleteSnapshotId && (
                              <button
                                onClick={() => handleRollback(deleteSnapshotId)}
                                disabled={isRollingBackDelete}
                                className="ml-auto flex items-center gap-1 text-[10px] text-warning-600 hover:text-warning-700 font-medium transition-colors disabled:opacity-50"
                                title="Annulla eliminazione"
                              >
                                {isRollingBackDelete ? (
                                  <Loader2 className="w-3 h-3 animate-spin" />
                                ) : (
                                  <Undo2 className="w-3 h-3" />
                                )}
                                Annulla
                              </button>
                            )}
                            {deleteRolledBack && (
                              <span className="ml-auto flex items-center gap-1 text-[10px] text-slate-400 font-medium">
                                <Check className="w-3 h-3" />
                                Ripristinato
                              </span>
                            )}
                          </div>
                        )
                      }

                      // Delete with constraint violation (error)
                      if (isDelete && !result.success) {
                        return (
                          <div
                            key={i}
                            className="flex items-center gap-2 py-1.5 px-3 rounded-md text-xs bg-warning-50 border border-warning-100"
                          >
                            <XCircle className="w-3.5 h-3.5 text-warning-600 flex-shrink-0" />
                            <Trash2 className="w-3.5 h-3.5 text-slate-500 flex-shrink-0" />
                            <span className="text-warning-600">
                              {result.error ?? `Errore eliminazione ${result.name}`}
                            </span>
                          </div>
                        )
                      }

                      return (
                        <div
                          key={i}
                          className={cn(
                            'flex items-center gap-2 py-1.5 px-3 rounded-md text-xs',
                            isRolledBack
                              ? 'bg-slate-50 border border-slate-200'
                              : result.success
                                ? 'bg-success-50 border border-success-200'
                                : 'bg-danger-50 border border-danger-200'
                          )}
                        >
                          {isRolledBack ? (
                            <Check className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                          ) : result.success ? (
                            <CheckCircle2 className="w-3.5 h-3.5 text-success-600 flex-shrink-0" />
                          ) : (
                            <XCircle className="w-3.5 h-3.5 text-danger-600 flex-shrink-0" />
                          )}
                          {isUpdate ? (
                            <Pencil className="w-3.5 h-3.5 text-slate-500 flex-shrink-0" />
                          ) : result.action === 'create_client' || result.action === 'upsert_client' ? (
                            <UserPlus className="w-3.5 h-3.5 text-slate-500 flex-shrink-0" />
                          ) : result.action === 'create_task' ? (
                            <ListChecks className="w-3.5 h-3.5 text-slate-500 flex-shrink-0" />
                          ) : result.action === 'update_procedures' ? (
                            <BookOpen className="w-3.5 h-3.5 text-slate-500 flex-shrink-0" />
                          ) : (
                            <FolderPlus className="w-3.5 h-3.5 text-slate-500 flex-shrink-0" />
                          )}
                          <span className={isRolledBack ? 'text-slate-400 line-through' : result.success ? 'text-success-700' : 'text-danger-700'}>
                            {result.success
                              ? result.action === 'update_procedures'
                                ? result.name
                                : `${entityLabel(result.action)}: ${result.name}`
                              : `Errore ${entityLabel(result.action).toLowerCase()}: ${result.error ?? result.name}`
                            }
                          </span>
                          {/* Undo button for update actions */}
                          {result.success && result.snapshotId && !isRolledBack && (
                            <button
                              onClick={() => handleRollback(result.snapshotId!)}
                              disabled={isRollingBackThis}
                              className="ml-auto flex items-center gap-1 text-[10px] text-warning-600 hover:text-warning-700 font-medium transition-colors disabled:opacity-50"
                              title="Annulla questa modifica"
                            >
                              {isRollingBackThis ? (
                                <Loader2 className="w-3 h-3 animate-spin" />
                              ) : (
                                <Undo2 className="w-3 h-3" />
                              )}
                              Annulla
                            </button>
                          )}
                          {isRolledBack && (
                            <span className="ml-auto flex items-center gap-1 text-[10px] text-slate-400 font-medium">
                              <Check className="w-3 h-3" />
                              Annullato
                            </span>
                          )}
                          {result.success && !result.snapshotId && !result.pendingDeleteId && (() => {
                            const target = actionTarget(result)
                            return target ? (
                              <button
                                onClick={() => navigate(target)}
                                className="ml-auto text-[10px] text-success-600 hover:text-success-700 underline underline-offset-2"
                              >
                                Vai →
                              </button>
                            ) : null
                          })()}
                        </div>
                      )
                    })}
                    {message.isProcessingActions && (
                      <div className="flex items-center gap-2 py-1.5 px-3">
                        <Loader2 className="w-3 h-3 animate-spin text-warning-600" />
                        <span className="text-[11px] text-warning-600">Elaborazione in corso...</span>
                      </div>
                    )}
                  </div>
                )}

                {/* Streaming cursor */}
                {message.isStreaming && message.content && (
                  <span className="inline-block w-1.5 h-4 bg-accent-600 animate-pulse ml-0.5 align-text-bottom rounded-sm" />
                )}

                {/* Empty streaming state — only show if no thinking content yet */}
                {message.isStreaming && !message.content && !message.thinkingContent && (
                  <div className="flex items-center gap-2 text-slate-400">
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    <span className="text-xs">Sto pensando...</span>
                  </div>
                )}

                <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                  <p className={cn(
                    'text-[10px] tabular-nums',
                    message.role === 'assistant' ? (isError ? 'text-danger-500' : 'text-slate-400') : 'text-white/70'
                  )}>
                    {message.timestamp.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}
                  </p>
                  {message.durationMs && (
                    <p className="text-[10px] text-slate-400 tabular-nums">
                      {(message.durationMs / 1000).toFixed(1)}s
                    </p>
                  )}
                  {message.role === 'assistant' && message.model && !message.isStreaming && (
                    <span className={cn(
                      'inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[9px] font-medium border',
                      tierStyle(message.tier)
                    )}>
                      {shortModelName(message.model)}
                      {message.tier && (
                        <span className="opacity-60">· {tierLabel(message.tier)}</span>
                      )}
                      {message.cost != null && (
                        <span className="opacity-60 tabular-nums">
                          · {message.cost === 0
                            ? 'Gratis'
                            : `${message.tier === 'gemini' ? '~' : ''}$${message.cost < 0.01 ? message.cost.toFixed(5) : message.cost.toFixed(3)}${message.tier === 'gemini' ? ' stima' : ''}`}
                        </span>
                      )}
                    </span>
                  )}
                </div>

                {/* Edit button for user messages */}
                {message.role === 'user' && !isStreaming && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      editMessage(message.id)
                    }}
                    className="opacity-0 group-hover:opacity-100 absolute -left-1 -top-1 p-1 bg-white border border-slate-200 rounded-md shadow-sm hover:bg-slate-50 transition-all"
                    title="Modifica messaggio"
                  >
                    <Pencil className="w-3 h-3 text-slate-400" />
                  </button>
                )}

                {/* Copy button for assistant messages */}
                {message.role === 'assistant' && message.id !== 'welcome' && !message.isStreaming && message.content && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      navigator.clipboard.writeText(message.content)
                      setCopiedId(message.id)
                      setTimeout(() => setCopiedId(null), 2000)
                    }}
                    className="opacity-0 group-hover:opacity-100 absolute -right-1 -top-1 p-1 bg-white border border-slate-200 rounded-md shadow-sm hover:bg-slate-50 transition-all"
                    title="Copia risposta"
                  >
                    {copiedId === message.id ? (
                      <Check className="w-3 h-3 text-success-600" />
                    ) : (
                      <Copy className="w-3 h-3 text-slate-400" />
                    )}
                  </button>
                )}
              </div>
            </div>
          )
        })}

        <div ref={messagesEndRef} />
      </div>

      {/* Quick actions */}
      <div className="flex items-center gap-2 flex-wrap mb-2 mt-3">
        {[
          { label: 'Suggerisci copy', icon: Zap, action: 'Suggerisci 3 varianti di copy per un post Instagram sul tema marketing digitale' },
          { label: 'Hashtag', icon: Sparkles, action: 'Suggerisci hashtag per un post sul marketing digitale su Instagram' },
          { label: 'Analisi performance', icon: Zap, action: 'Analizza le performance delle campagne ADV di questo mese e suggerisci miglioramenti' },
          { label: 'Report settimanale', icon: Zap, action: 'Genera un report settimanale sulle attivita\' completate e quelle in corso' },
        ].map((chip) => (
          <button
            key={chip.label}
            onClick={() => sendMessage(chip.action)}
            disabled={isStreaming}
            className={cn(
              'flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium border transition-colors',
              isStreaming
                ? 'border-slate-200 text-slate-300 cursor-not-allowed'
                : 'border-slate-200 text-slate-600 hover:bg-slate-50 hover:border-slate-300'
            )}
          >
            <chip.icon className="w-3 h-3" />
            {chip.label}
          </button>
        ))}
      </div>

      {/* Input area */}
      <div className="flex-shrink-0">
        <div className="bg-white rounded-2xl border border-slate-200 shadow-card">
          {/* Preview file pendenti */}
          {pendingFiles.length > 0 && (
            <div className="flex items-center gap-2 px-3 pt-3 pb-1 flex-wrap">
              {pendingFiles.map((file, i) => (
                <div key={i} className="relative group/file flex items-center gap-1.5 bg-slate-50 border border-slate-200 rounded-md px-2 py-1">
                  {file.type.startsWith('image/') && pendingPreviews[i] ? (
                    <img
                      src={pendingPreviews[i]!}
                      alt={file.name}
                      className="w-8 h-8 object-cover rounded"
                    />
                  ) : (
                    <FileText className="w-4 h-4 text-slate-500" />
                  )}
                  <span className="text-[11px] text-slate-600 truncate max-w-[100px]">{file.name}</span>
                  <button
                    onClick={() => removePendingFile(i)}
                    className="ml-0.5 p-0.5 rounded-full hover:bg-slate-200 transition-colors"
                    aria-label={`Rimuovi ${file.name}`}
                  >
                    <X className="w-3 h-3 text-slate-400" />
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="flex items-end gap-3 p-3">
            <div className="flex-1">
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Scrivi un messaggio... (Invio per inviare, Shift+Invio per andare a capo)"
                rows={1}
                className={cn(
                  'w-full resize-none text-sm text-slate-800 placeholder:text-slate-400',
                  'focus:outline-none bg-transparent leading-relaxed',
                  'max-h-32 overflow-y-auto'
                )}
                style={{ minHeight: '24px' }}
              />
            </div>

            {/* Hidden file input */}
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/jpeg,image/png,image/gif,image/webp,application/pdf"
              onChange={handleFileSelect}
              className="hidden"
            />

            {/* Paperclip button */}
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={isStreaming}
              className={cn(
                'flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center transition-colors',
                isStreaming
                  ? 'text-slate-300 cursor-not-allowed'
                  : 'text-slate-400 hover:text-slate-600 hover:bg-slate-100'
              )}
              aria-label="Allega file"
              title="Allega immagine o PDF"
            >
              <Paperclip className="w-4 h-4" />
            </button>

            {/* Send / Stop button */}
            {isStreaming ? (
              <button
                onClick={handleStop}
                className="flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center transition-colors bg-danger-600 text-white hover:bg-danger-700"
                aria-label="Ferma generazione"
                title="Ferma la generazione"
              >
                <Square className="w-3.5 h-3.5 fill-current" />
              </button>
            ) : (
              <button
                onClick={handleSend}
                disabled={!input.trim() && pendingFiles.length === 0}
                className={cn(
                  'flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center transition-colors',
                  input.trim() || pendingFiles.length > 0
                    ? 'bg-primary-600 text-white hover:bg-primary-700'
                    : 'bg-slate-100 text-slate-400 cursor-not-allowed'
                )}
                aria-label="Invia messaggio"
              >
                <Send className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
        <p className="text-[11px] text-slate-400 mt-1.5 text-center">
          AI Assistant in fase beta. Le risposte potrebbero non essere accurate.
        </p>
      </div>
    </div>
  )
}
